import React, { useId } from 'react';
import { ServicePlan } from './serviceService';

export const planTierLabel = (plan: ServicePlan) => plan.tier?.trim()
  || plan.name.replace(/\s*·\s*\d+\s*мес\.?$/i, '').trim()
  || 'Стандартный';

const tierKey = (plan: ServicePlan) => planTierLabel(plan).toLocaleLowerCase('ru-RU');

export const planDurationLabel = (months?: number | null) => {
  if (!months) return 'Срок не указан';
  const lastTwo = months % 100;
  const last = months % 10;
  const word = lastTwo >= 11 && lastTwo <= 14 ? 'месяцев'
    : last === 1 ? 'месяц' : last >= 2 && last <= 4 ? 'месяца' : 'месяцев';
  return `${months} ${word}`;
};

const choiceStyle = 'flex min-h-[44px] cursor-pointer items-center justify-center rounded-xl border border-transparent px-4 py-2.5 text-center text-sm font-semibold leading-5 transition-colors hover:bg-foreground/5 peer-checked:border-red-500/30 peer-checked:bg-red-600 peer-checked:text-white peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-red-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card';

export default function ServicePlanSelector({ plans, selected, onSelect }: {
  plans: ServicePlan[];
  selected: ServicePlan;
  onSelect: (id: number) => void;
}) {
  const groupId = useId();
  const tiers = Array.from(new Map(plans.map(plan => [tierKey(plan), planTierLabel(plan)])).entries());
  const tierPlans = plans.filter(plan => tierKey(plan) === tierKey(selected))
    .sort((a, b) => (a.durationMonths ?? Infinity) - (b.durationMonths ?? Infinity));

  const selectTier = (key: string) => {
    const options = plans.filter(plan => tierKey(plan) === key);
    const sameDuration = options.find(plan => (plan.durationMonths ?? null) === (selected.durationMonths ?? null));
    const closest = sameDuration ?? options.sort((a, b) =>
      Math.abs((a.durationMonths ?? Infinity) - (selected.durationMonths ?? 0))
      - Math.abs((b.durationMonths ?? Infinity) - (selected.durationMonths ?? 0)))[0];
    onSelect(closest.id);
  };

  return (
    <div className="space-y-5">
      <fieldset className="min-w-0">
        <legend className="mb-2.5 text-sm font-semibold text-foreground/70">Тариф</legend>
        <div className="flex flex-wrap gap-1 rounded-2xl border border-foreground/10 bg-foreground/[0.03] p-1.5">
          {tiers.map(([key, label]) => (
            <label key={key} className="min-w-0 flex-1 basis-28">
              <input type="radio" name={`${groupId}-tier`} value={key} checked={key === tierKey(selected)}
                onChange={() => selectTier(key)} className="peer sr-only" />
              <span className={`${choiceStyle} break-words`}>{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {tierPlans.some(plan => plan.durationMonths != null) && (
        <fieldset className="min-w-0">
          <legend className="mb-2.5 text-sm font-semibold text-foreground/70">Срок</legend>
          <div className="flex flex-wrap gap-1 rounded-2xl border border-foreground/10 bg-foreground/[0.03] p-1.5">
            {tierPlans.map(plan => (
              <label key={plan.id} className="min-w-0 flex-1 basis-24">
                <input type="radio" name={`${groupId}-duration`} value={plan.id} checked={plan.id === selected.id}
                  onChange={() => onSelect(plan.id)} className="peer sr-only" />
                <span className={choiceStyle}>{planDurationLabel(plan.durationMonths)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}
